import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../utils/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from './ui/dialog';
import { Mail, Phone, ShieldCheck, Loader2, CheckCircle2, AlertCircle, Send, X } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Iter-161 — Talent Contact Editing Panel.
 *
 * Lives inside /talent/profile (Info & Security tab). Lets talent update
 * their login email and phone with proper verification:
 *   - Email: SendGrid magic-link (24h)
 *   - Phone: Twilio SMS OTP (10 min, 6 digits)
 *
 * Existing JWT session is preserved during pending verification — talent
 * stays logged in.
 */
const ContactInfoPanel = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [me, setMe] = useState(null);
  const [pending, setPending] = useState([]); // [{id, contact_type, ...}]
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(null); // { type:'email'|'phone' }
  const [newValue, setNewValue] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // Shows ONLY after a change is fully verified ('email' | 'phone' | null).
  const [successBanner, setSuccessBanner] = useState(null);

  // OTP entry dialog (phone)
  const [otpDialog, setOtpDialog] = useState(null); // { request_id, masked, expires_at }
  const [otpCode, setOtpCode] = useState('');
  const [otpSubmitting, setOtpSubmitting] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [meRes, pendingRes] = await Promise.all([
        api.get('/auth/me'),
        api.get('/contacts/pending'),
      ]);
      setMe(meRes.data);
      setPending(pendingRes.data?.pending || []);
    } catch (e) {
      console.error('Failed to load contact info', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Email verification completes off-page (magic link → /verify-contact),
  // which routes back here with ?contact_verified=email. Surface the same
  // confirmation banner, then strip the param so it doesn't re-show on reload.
  useEffect(() => {
    const verified = searchParams.get('contact_verified');
    if (verified === 'email' || verified === 'phone') {
      setSuccessBanner(verified);
      const next = new URLSearchParams(searchParams);
      next.delete('contact_verified');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Cooldown countdown
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  const pendingEmail = pending.find((p) => p.contact_type === 'email');
  const pendingPhone = pending.find((p) => p.contact_type === 'phone');

  const openDialog = (type) => {
    setNewValue('');
    setSuccessBanner(null);
    setDialog({ type });
  };

  const submitChange = async () => {
    if (!dialog) return;
    if (!newValue.trim()) {
      toast.error('Enter a new value first');
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post('/contacts/request-change', {
        contact_type: dialog.type,
        new_value: newValue.trim(),
      });
      setDialog(null);
      if (dialog.type === 'phone') {
        // Show OTP entry dialog
        setOtpDialog({
          request_id: res.data.request_id,
          masked: res.data.masked_new_value,
          expires_at: res.data.expires_at,
        });
        setOtpCode('');
        setResendCooldown(30);
      } else {
        toast.success('Verification email sent — tap the link in your inbox to confirm.');
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to send verification');
    } finally {
      setSubmitting(false);
    }
  };

  const submitOtp = async () => {
    if (!otpDialog) return;
    if (otpCode.length !== 6 || !/^\d{6}$/.test(otpCode)) {
      toast.error('Enter the 6-digit code from your text');
      return;
    }
    setOtpSubmitting(true);
    try {
      await api.post('/contacts/verify', {
        request_id: otpDialog.request_id,
        code: otpCode,
      });
      toast.success('Phone updated successfully');
      setSuccessBanner('phone');
      setOtpDialog(null);
      setOtpCode('');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Verification failed');
    } finally {
      setOtpSubmitting(false);
    }
  };

  const resend = async (requestId) => {
    if (resendCooldown > 0) return;
    try {
      const res = await api.post('/contacts/resend', { request_id: requestId });
      toast.success(res.data?.message || 'Code resent');
      setResendCooldown(30);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to resend');
    }
  };

  const cancel = async (requestId) => {
    if (!window.confirm('Cancel this pending verification?')) return;
    try {
      await api.post('/contacts/cancel', { request_id: requestId });
      toast.success('Verification cancelled');
      if (otpDialog?.request_id === requestId) setOtpDialog(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to cancel');
    }
  };

  if (loading) {
    return (
      <Card data-testid="contact-info-panel-loading">
        <CardContent className="p-6 flex items-center justify-center">
          <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="contact-info-panel">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <ShieldCheck className="w-4 h-4 text-cyan-600" />
          Contact &amp; Login
        </CardTitle>
        <CardDescription className="text-xs">
          Update the email and phone you use to log in and receive shift notifications.
          Both require verification before the change takes effect.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Success confirmation — shows ONLY after a change is fully verified,
            never while a new email/phone is still pending verification. */}
        {successBanner && (
          <div
            className="bg-emerald-50 border border-emerald-200 rounded-md p-3 flex items-start gap-2"
            data-testid="contact-verified-banner"
          >
            <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
            <p className="text-xs text-emerald-900 flex-1 min-w-0">
              Contact information updated successfully. Your booking invitations,
              shift reminders, and other notifications will be sent to your verified
              email and phone number.
            </p>
            <button
              type="button"
              onClick={() => setSuccessBanner(null)}
              className="text-emerald-700 hover:text-emerald-900 shrink-0"
              aria-label="Dismiss"
              data-testid="contact-verified-banner-dismiss"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
        {/* Email row */}
        <div className="border border-gray-200 rounded-lg p-4 space-y-2" data-testid="contact-email-row">
          <div className="flex items-start justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-2 min-w-0">
              <Mail className="w-4 h-4 text-gray-500 shrink-0" />
              <div className="min-w-0">
                <p className="text-xs font-semibold uppercase text-gray-500 tracking-wide">Email</p>
                <p className="text-sm font-medium text-gray-900 truncate" data-testid="contact-current-email">
                  {me?.email || '—'}
                </p>
              </div>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={() => openDialog('email')}
              disabled={!!pendingEmail}
              data-testid="edit-email-btn"
            >
              {pendingEmail ? 'Pending verification' : 'Change'}
            </Button>
          </div>
          {pendingEmail && (
            <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-xs space-y-2" data-testid="email-pending-banner">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-3.5 h-3.5 text-amber-600 shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-amber-900 font-medium">
                    Pending: <span className="font-mono">{pendingEmail.display_value}</span>
                  </p>
                  <p className="text-amber-800 mt-0.5">
                    Tap the link in your inbox to confirm. Expires {new Date(pendingEmail.expires_at).toLocaleString()}.
                  </p>
                </div>
              </div>
              <div className="flex gap-2 flex-wrap">
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 text-xs"
                  onClick={() => resend(pendingEmail.id)}
                  disabled={resendCooldown > 0}
                  data-testid="resend-email-btn"
                >
                  <Send className="w-3 h-3 mr-1" />
                  {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend link'}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 text-xs border-rose-200 text-rose-700 hover:bg-rose-50"
                  onClick={() => cancel(pendingEmail.id)}
                  data-testid="cancel-email-btn"
                >
                  <X className="w-3 h-3 mr-1" /> Cancel
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Phone row */}
        <div className="border border-gray-200 rounded-lg p-4 space-y-2" data-testid="contact-phone-row">
          <div className="flex items-start justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-2 min-w-0">
              <Phone className="w-4 h-4 text-gray-500 shrink-0" />
              <div className="min-w-0">
                <p className="text-xs font-semibold uppercase text-gray-500 tracking-wide">Phone</p>
                <p className="text-sm font-medium text-gray-900 truncate" data-testid="contact-current-phone">
                  {me?.phone || '—'}
                </p>
              </div>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={() => openDialog('phone')}
              disabled={!!pendingPhone}
              data-testid="edit-phone-btn"
            >
              {pendingPhone ? 'Pending verification' : 'Change'}
            </Button>
          </div>
          {pendingPhone && (
            <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-xs space-y-2" data-testid="phone-pending-banner">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-3.5 h-3.5 text-amber-600 shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-amber-900 font-medium">
                    Pending: <span className="font-mono">{pendingPhone.display_value}</span>
                  </p>
                  <p className="text-amber-800 mt-0.5">
                    Enter the 6-digit code from your text. Expires {new Date(pendingPhone.expires_at).toLocaleString()}.
                  </p>
                </div>
              </div>
              <div className="flex gap-2 flex-wrap">
                <Button
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() => setOtpDialog({
                    request_id: pendingPhone.id,
                    masked: pendingPhone.display_value,
                    expires_at: pendingPhone.expires_at,
                  })}
                  data-testid="enter-otp-btn"
                >
                  Enter code
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 text-xs"
                  onClick={() => resend(pendingPhone.id)}
                  disabled={resendCooldown > 0}
                  data-testid="resend-phone-btn"
                >
                  <Send className="w-3 h-3 mr-1" />
                  {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend code'}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 text-xs border-rose-200 text-rose-700 hover:bg-rose-50"
                  onClick={() => cancel(pendingPhone.id)}
                  data-testid="cancel-phone-btn"
                >
                  <X className="w-3 h-3 mr-1" /> Cancel
                </Button>
              </div>
            </div>
          )}
        </div>
      </CardContent>

      {/* Request-change dialog */}
      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent data-testid="contact-edit-dialog">
          <DialogHeader>
            <DialogTitle>
              Update {dialog?.type === 'email' ? 'Email' : 'Phone'}
            </DialogTitle>
            <DialogDescription>
              {dialog?.type === 'email'
                ? "We'll email a verification link to your new address. The change is final once you tap the link."
                : "We'll text a 6-digit code to your new number. Enter it on the next screen to confirm."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="new-contact-input">
              New {dialog?.type === 'email' ? 'email address' : 'mobile number'}
            </Label>
            <Input
              id="new-contact-input"
              type={dialog?.type === 'email' ? 'email' : 'tel'}
              value={newValue}
              onChange={(e) => setNewValue(e.target.value)}
              placeholder={dialog?.type === 'email' ? 'you@example.com' : '(404) 555-1212'}
              data-testid="new-contact-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(null)} disabled={submitting} data-testid="contact-edit-cancel">
              Cancel
            </Button>
            <Button onClick={submitChange} disabled={submitting} data-testid="contact-edit-submit">
              {submitting && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
              Send verification
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* OTP entry dialog */}
      <Dialog open={!!otpDialog} onOpenChange={(o) => !o && setOtpDialog(null)}>
        <DialogContent data-testid="otp-dialog">
          <DialogHeader>
            <DialogTitle>Enter your verification code</DialogTitle>
            <DialogDescription>
              We texted a 6-digit code to <span className="font-mono">{otpDialog?.masked}</span>.
              It expires in 10 minutes.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Input
              type="text"
              inputMode="numeric"
              maxLength={6}
              value={otpCode}
              onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="000000"
              className="text-center text-2xl font-mono tracking-widest"
              data-testid="otp-code-input"
            />
            <div className="flex items-center justify-between">
              <button
                type="button"
                className="text-xs text-cyan-700 hover:underline disabled:text-gray-400"
                onClick={() => otpDialog && resend(otpDialog.request_id)}
                disabled={resendCooldown > 0}
                data-testid="otp-resend-btn"
              >
                {resendCooldown > 0 ? `Resend code in ${resendCooldown}s` : 'Resend code'}
              </button>
              <button
                type="button"
                className="text-xs text-rose-700 hover:underline"
                onClick={() => otpDialog && cancel(otpDialog.request_id)}
                data-testid="otp-cancel-btn"
              >
                Cancel
              </button>
            </div>
          </div>
          <DialogFooter>
            <Button
              onClick={submitOtp}
              disabled={otpSubmitting || otpCode.length !== 6}
              data-testid="otp-submit-btn"
              className="w-full"
            >
              {otpSubmitting && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
              <CheckCircle2 className="w-4 h-4 mr-1" />
              Verify &amp; update
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default ContactInfoPanel;
